import { ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { describeSharePointLink } from '@/cloud/models/sharepoint-link';
import { TranslationService } from '@/layout/service/translation.service';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { SharePointLinkTileComponent } from '../sharepoint-link/sharepoint-link-tile.component';
import { LocationWorkingHoursComponent } from '../working-hours/location-working-hours.component';
import { LocationGeoComponent } from '../location-geo/location-geo.component';
import { LocationFeedbackSettingsComponent } from '../location-feedback-settings/location-feedback-settings.component';

/**
 * The Settings tab of any location (building, floor or room): the SharePoint folder its Documents tab links to, its
 * map location, the working hours for room analytics and, for rooms, whether ratings are offered. The locations below inherit the map location and hours.
 */
@Component({
    selector: 'app-location-settings',
    standalone: true,
    imports: [FormsModule, ButtonModule, InputTextModule, MessageModule, SectionCardComponent, SharePointLinkTileComponent, LocationWorkingHoursComponent, LocationGeoComponent, LocationFeedbackSettingsComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="location-settings">
            <app-section-card [title]="t()('sharePoint.title')" [subtitle]="t()('sharePoint.description')" icon="pi pi-microsoft">
                @if (editing()) {
                    <div class="sp-edit" (keydown.escape)="cancel()">
                        <label for="sharepoint-url">{{ t()('sharePoint.url') }}</label>
                        <div class="sp-edit__field">
                            <input
                                #urlInput
                                pInputText
                                id="sharepoint-url"
                                type="url"
                                inputmode="url"
                                autocomplete="off"
                                spellcheck="false"
                                maxlength="2048"
                                [placeholder]="t()('sharePoint.placeholder')"
                                [ngModel]="draft()"
                                (ngModelChange)="draft.set($event)"
                                (keydown.enter)="canSave() && saveSharePointUrl()"
                                [disabled]="saving()"
                                [attr.aria-invalid]="!validDraft()"
                                aria-describedby="sharepoint-url-help"
                            />
                            @if (canPaste) {
                                <p-button icon="pi pi-clipboard" [label]="t()('sharePoint.paste')" severity="secondary" [outlined]="true" [disabled]="saving()" (onClick)="paste()" />
                            }
                        </div>
                        <p id="sharepoint-url-help" class="sp-edit__help"><i class="pi pi-info-circle" aria-hidden="true"></i>{{ t()('sharePoint.help') }}</p>
                        @if (draftLink(); as preview) {
                            <app-sharepoint-link-tile [link]="preview" variant="preview" />
                        } @else if (draft().trim()) {
                            <p-message severity="warn" icon="pi pi-exclamation-triangle">{{ t()('sharePoint.invalid') }}</p-message>
                        }
                        @if (error()) {
                            <p-message severity="error" role="alert">{{ error() }}</p-message>
                        }
                        <div class="sp-edit__footer">
                            <span class="sp-edit__keys" aria-hidden="true"><kbd>Enter</kbd> {{ t()('sharePoint.save') }} · <kbd>Esc</kbd> {{ t()('sharePoint.cancel') }}</span>
                            <p-button [label]="t()('sharePoint.cancel')" severity="secondary" [text]="true" [disabled]="saving()" (onClick)="cancel()" />
                            <p-button [label]="t()('sharePoint.save')" icon="pi pi-check" [loading]="saving()" [disabled]="!canSave()" (onClick)="saveSharePointUrl()" />
                        </div>
                    </div>
                } @else if (currentLink(); as link) {
                    <app-sharepoint-link-tile [link]="link">
                        @if (canManage()) {
                            <p-button icon="pi pi-pencil" [text]="true" [rounded]="true" severity="secondary" [ariaLabel]="t()('sharePoint.edit')" (onClick)="edit()" />
                            <p-button icon="pi pi-trash" [text]="true" [rounded]="true" severity="danger" [ariaLabel]="t()('sharePoint.remove')" (onClick)="confirmingRemove.set(true)" />
                        }
                    </app-sharepoint-link-tile>
                    @if (confirmingRemove()) {
                        <div class="sp-remove" role="alertdialog" aria-labelledby="sp-remove-text">
                            <span id="sp-remove-text">{{ t()('sharePoint.removeConfirm') }}</span>
                            <p-button [label]="t()('sharePoint.cancel')" severity="secondary" [text]="true" size="small" [disabled]="saving()" (onClick)="confirmingRemove.set(false)" />
                            <p-button [label]="t()('sharePoint.remove')" severity="danger" size="small" [loading]="saving()" (onClick)="remove()" />
                        </div>
                    }
                    @if (error()) {
                        <p-message severity="error" role="alert">{{ error() }}</p-message>
                    }
                } @else {
                    <div class="sp-empty">
                        <span class="sp-empty__icon" aria-hidden="true"><i class="pi pi-folder-plus"></i></span>
                        <div>
                            <strong>{{ t()('sharePoint.notConfigured') }}</strong>
                            <p>{{ t()(canManage() ? 'sharePoint.emptyHint' : 'sharePoint.emptyReadOnly') }}</p>
                        </div>
                        @if (canManage()) {
                            <p-button [label]="t()('sharePoint.add')" icon="pi pi-link" (onClick)="edit()" />
                        }
                    </div>
                }
            </app-section-card>
            <app-section-card [title]="t()('locationGeo.title')" [subtitle]="t()('locationGeo.description')" icon="pi pi-map-marker">
                <app-location-geo [locationId]="locationId()" [locationName]="locationName()" [canManage]="canManage()" />
            </app-section-card>
            <app-section-card [title]="t()('locationHours.title')" [subtitle]="t()('locationSettings.workingHoursDetail')" icon="fa fa-clock">
                <app-location-working-hours [locationId]="locationId()" [locationName]="locationName()" [canManage]="canManage()" />
            </app-section-card>
            @if (isRoom()) {
                <app-section-card [title]="t()('feedbackSettings.title')" [subtitle]="t()('feedbackSettings.roomHelp')" icon="pi pi-star">
                    <app-location-feedback-settings [locationId]="locationId()" [canManage]="canManage()" />
                </app-section-card>
            }
        </div>
    `,
    styles: [
        `
            .location-settings {
                display: grid;
                gap: var(--app-space-4, 1rem);
                max-width: 48rem;
            }
            .sp-edit {
                display: grid;
                gap: 0.75rem;
                label {
                    font-weight: 600;
                }
            }
            .sp-edit__field {
                display: flex;
                gap: 0.5rem;
                input {
                    flex: 1;
                    min-width: 0;
                }
            }
            .sp-edit__help {
                display: flex;
                gap: 0.5rem;
                margin: 0;
                font-size: 0.8125rem;
                line-height: 1.5;
                color: var(--text-color-secondary);
                i {
                    margin-top: 0.2rem;
                    font-size: 0.75rem;
                }
            }
            .sp-edit__footer {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                flex-wrap: wrap;
                gap: 0.5rem;
            }
            .sp-edit__keys {
                margin-inline-end: auto;
                font-size: 0.75rem;
                color: var(--text-color-secondary);
                kbd {
                    padding: 0.05rem 0.35rem;
                    border: 1px solid var(--surface-border);
                    border-radius: 0.3rem;
                    background: var(--surface-ground);
                    font: inherit;
                }
            }
            .sp-remove {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 0.5rem;
                margin-top: 0.75rem;
                padding: 0.625rem 0.875rem;
                border-radius: 0.65rem;
                background: var(--app-status-offline-bg, var(--surface-ground));
                font-size: 0.875rem;
                span {
                    flex: 1;
                    min-width: 12rem;
                }
            }
            .sp-empty {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 1rem;
                padding: 1rem;
                border: 1px dashed var(--surface-border);
                border-radius: 0.75rem;
                > div {
                    flex: 1;
                    min-width: 12rem;
                }
                p {
                    margin: 0.25rem 0 0;
                    font-size: 0.8125rem;
                    color: var(--text-color-secondary);
                }
            }
            .sp-empty__icon {
                display: grid;
                place-items: center;
                width: 2.75rem;
                height: 2.75rem;
                border-radius: 0.65rem;
                background: var(--surface-ground);
                color: var(--text-color-secondary);
                font-size: 1.15rem;
            }
            @media (max-width: 30rem) {
                .sp-edit__keys {
                    display: none;
                }
            }
        `
    ]
})
export class LocationSettingsComponent {
    private readonly api = inject(CloudApiService);
    readonly t = inject(TranslationService).t;

    readonly locationId = input.required<string>();
    readonly locationName = input('');
    readonly sharePointUrl = input<string | null>(null);
    readonly sharePointUrlChange = output<string | null>();
    readonly canManage = input(false);
    /** Rooms also get their own ratings (feedback) setting. */
    readonly isRoom = input(false);

    readonly urlInput = viewChild<ElementRef<HTMLInputElement>>('urlInput');
    readonly canPaste = typeof navigator !== 'undefined' && !!navigator.clipboard?.readText;
    readonly editing = signal(false);
    readonly confirmingRemove = signal(false);
    readonly draft = signal('');
    readonly saving = signal(false);
    readonly error = signal('');

    readonly currentLink = computed(() => describeSharePointLink(this.sharePointUrl()));
    readonly draftLink = computed(() => describeSharePointLink(this.draft()));
    readonly validDraft = computed(() => !this.draft().trim() || !!this.draftLink());
    readonly canSave = computed(() => !this.saving() && this.validDraft() && this.draft().trim() !== (this.sharePointUrl() ?? ''));

    constructor() {
        // A save elsewhere (or a live update) replaces the link; drop any half-finished edit of the old one.
        effect(() => {
            const value = this.sharePointUrl();
            untracked(() => {
                this.draft.set(value ?? '');
                this.editing.set(false);
                this.confirmingRemove.set(false);
            });
        });
        effect(() => {
            if (this.editing()) queueMicrotask(() => this.urlInput()?.nativeElement.focus());
        });
    }

    edit(): void {
        this.draft.set(this.sharePointUrl() ?? '');
        this.error.set('');
        this.confirmingRemove.set(false);
        this.editing.set(true);
    }

    cancel(): void {
        if (this.saving()) return;
        this.editing.set(false);
        this.draft.set(this.sharePointUrl() ?? '');
        this.error.set('');
    }

    async paste(): Promise<void> {
        try {
            const text = (await navigator.clipboard.readText()).trim();
            if (text) this.draft.set(text);
        } catch {
            // Clipboard read refused; typing or Ctrl+V still works.
        }
        this.urlInput()?.nativeElement.focus();
    }

    async remove(): Promise<void> {
        this.draft.set('');
        await this.saveSharePointUrl();
    }

    async saveSharePointUrl(): Promise<void> {
        if (!this.canManage() || this.saving() || !this.validDraft()) return;
        this.saving.set(true);
        this.error.set('');
        try {
            const response = await this.api.updateLocationSharePointLink(this.locationId(), this.draft().trim() || null);
            this.editing.set(false);
            this.confirmingRemove.set(false);
            this.sharePointUrlChange.emit(response.sharePointUrl ?? null);
        } catch {
            this.error.set(this.t()('sharePoint.saveError'));
        } finally {
            this.saving.set(false);
        }
    }
}
